import { createSelector } from 'reselect';
import { get, find, isEmpty } from 'lodash/fp';
import { createScheduledShareIds } from 'app/lib/utils';
import { ErrorLevel } from 'app/lib/constants';
import {
    selectCurrentProjectId,
    selectProjectSlugByEntityId
} from 'app/store/projects/selectors.js';
import { selectEntitiesById } from 'app/store/entities/selectors.js';
import { getShareError } from './utils.js';

const selectPropEntityId = (_, props) => props.entityId;

const selectSlice = get('shares');

export const selectIsLoadingSchedules = createSelector(selectSlice, get('isLoadingSchedules'));
const selectEmailDialog = createSelector(selectSlice, get('dialogs.email'));
export const selectEmailDialogIsOpen = createSelector(selectEmailDialog, get('isOpen'));
export const selectEmailDialogIsPending = createSelector(selectEmailDialog, get('isPending'));
export const selectEmailDialogMode = createSelector(selectEmailDialog, get('mode'));
export const selectEmailDialogShareSourceType = createSelector(
    selectEmailDialog,
    get('shareSourceType')
);
export const selectEmailDialogAssetId = createSelector(selectEmailDialog, get('assetId'));
export const selectEmailDialogValues = createSelector(selectEmailDialog, get('values'));

const selectSheetDialog = createSelector(selectSlice, get('dialogs.sheet'));
export const selectSheetDialogIsOpen = createSelector(selectSheetDialog, get('isOpen'));
export const selectSheetDialogIsPending = createSelector(selectSheetDialog, get('isPending'));
export const selectSheetDialogMode = createSelector(selectSheetDialog, get('mode'));
export const selectSheetDialogAssetId = createSelector(selectSheetDialog, get('assetId'));
export const selectSheetDialogValues = createSelector(selectSheetDialog, get('values'));
export const selectSheetError = createSelector(selectSheetDialog, get('error'));

export const selectSharesForCurrentProject = createSelector(
    selectSlice,
    selectEntitiesById,
    selectCurrentProjectId,
    (slice, entitiesById, projectId) =>
        slice.schedulesBySpaceUid[projectId]?.map((share) => {
            const { entityId } = createScheduledShareIds(share);
            const virtualEntity = entitiesById[entityId];
            return {
                ...share,
                lastRunAt: share.astRunAt ?? '',
                ...virtualEntity
            };
        })
);

export const selectSharesForEntityId = createSelector(
    selectSlice,
    selectEntitiesById,
    selectPropEntityId,
    selectProjectSlugByEntityId,
    (slice, entitiesById, entityId, projectSlug) => {
        const [projectId] = entityId ? entityId.split('.') : [];
        return slice.schedulesBySpaceUid[projectId]?.map((share) => {
            const { entityId } = createScheduledShareIds(share);
            const virtualEntity = entitiesById[entityId];
            return {
                ...share,
                lastRunAt: share.lastRunAt ?? '',
                ...virtualEntity,
                projectSlug
            };
        });
    }
);

export const selectShareByEntityId = createSelector(
    selectPropEntityId,
    selectSharesForEntityId,
    (entityPropId, shares) => find(['entityId', entityPropId], shares)
);

export const selectShareErrorsForCurrentProject = createSelector(
    selectSharesForCurrentProject,
    (shares) => {
        const result = {};
        shares
            ?.filter((share) => !isEmpty(share.lastRunError))
            .forEach((share) => {
                result[share.entityId] = {
                    name: share.name,
                    projectSlug: share.projectSlug,
                    uid: share.uid,
                    entityId: share.entityId,
                    errors: getShareError(share),
                    errorLevel: ErrorLevel.ERROR
                };
            });
        return result;
    }
);
